import { route } from 'preact-router';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useActingIdentity } from '../../hooks/useActingIdentity.js';
import { useFetch } from '../../hooks/useFetch.js';
import { type Notification, notificationId, useNotificationReads } from './notificationRead.js';
import { lookupFailedMessage } from './resultWording.js';

interface NotificationsResponse {
  items: Notification[];
  total: number;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * 078: read through `useFetch`, not a hand-rolled `useState` + `try/catch`.
   *
   * The previous read swallowed every error (`catch { // ignore }`) and left the list empty, so an
   * outage rendered as *"No notifications yet."* — an outage reported to the renter as good news,
   * which is 064's defect exactly. `FetchState` makes that unrepresentable: `data` is `null` on
   * every non-`ok` arm, so "content beside a failure" cannot be expressed at all.
   *
   * 🔴 085 (gap-list F6): the read is scoped to whoever the app is acting as.
   *
   * This component mounts in the app shell, **outside the `<Router>`**, and beginning or stopping
   * acting as another person changes the identity without navigating — so nothing unmounts it. Read
   * once on mount, it showed the administrator's inbox for the whole impersonation session, and the
   * renter's inbox after the administrator returned. Passing the identity is what re-issues the read
   * at that moment; the credential itself was always correct, because `api.ts` resolves it per
   * request. There is deliberately no timer (spec Assumption 3).
   */
  const actingIdentity = useActingIdentity();
  const state = useFetch<NotificationsResponse>('/notifications', actingIdentity);
  /**
   * 084: read tracking moved to `notificationRead.ts` when the notifications *screen* began doing
   * the same thing. Held in the client because the read does not poll — so the count must move
   * without asking the platform again. 085 adds the one thing that *does* re-ask: a change of
   * acting identity, which also clears what follows below.
   */
  const { isUnread, markError, open: openEntry } = useNotificationReads();
  const items = state.status === 'ok' ? state.data.items : [];

  /**
   * 🔴 FR-003: a count is derived ONLY from a read that succeeded.
   *
   * `items` is empty for two unrelated reasons — nothing to show, and nothing was read — and only
   * the first of them is a count of zero. Reading the length without checking the status is correct
   * by accident in four of the five states and a lie in the fifth.
   */
  const unread = state.status === 'ok' ? items.filter(isUnread).length : null;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div class="relative" ref={panelRef}>
      <button
        type="button"
        class="relative p-2 text-gray-400 hover:text-primary transition-colors"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-6 h-6"
          aria-hidden="true"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unread !== null && unread > 0 && (
          <span class="absolute top-1 right-1 bg-primary text-bg text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          class="absolute right-0 top-full mt-1 w-72 bg-surface-mid rounded-xl shadow-xl z-50 overflow-hidden"
        >
          <div class="px-4 py-3 border-b border-gray-800 text-sm font-semibold text-text">
            Notifications
          </div>
          {state.status === 'loading' && (
            <p class="px-4 py-3 text-sm text-gray-400">Loading notifications…</p>
          )}
          {/* 🔴 FR-008 / FR-010: a failed read says so, in the app's own words, and in a DIFFERENT
              sentence from the empty one. Retry is offered only where retrying can succeed — never
              for `gone`, `unauthenticated` or `refused`, which are terminal by construction
              (`classifyFetchError`: 404, 401, 403 and nothing else). */}
          {(state.status === 'failed' ||
            state.status === 'gone' ||
            state.status === 'unauthenticated' ||
            state.status === 'refused') && (
            <div class="px-4 py-3">
              <p class="text-sm text-gray-400">
                {lookupFailedMessage('Notifications')} {state.reason}
              </p>
              {state.status === 'failed' && (
                <button
                  type="button"
                  class="mt-2 text-sm text-primary underline"
                  onClick={() => state.retry()}
                >
                  Try again
                </button>
              )}
            </div>
          )}
          {state.status === 'ok' && !items.length && (
            <p class="px-4 py-3 text-sm text-gray-400">No notifications yet.</p>
          )}
          {markError && (
            <p role="alert" class="px-4 py-2 text-sm text-red-400">
              {markError}
            </p>
          )}
          <ul class="max-h-80 overflow-y-auto divide-y divide-gray-800">
            {items.map((n) => (
              <li key={notificationId(n)}>
                {/* #950: a real button, so an entry is reachable and openable from the keyboard. */}
                <button
                  type="button"
                  class={`block w-full text-left px-4 py-3 cursor-pointer hover:bg-surface-hi transition-colors ${isUnread(n) ? 'bg-surface-hi/50' : ''}`}
                  onClick={() => {
                    // 340: an entry about a booking opens it; the panel closes, as for "See all".
                    if (n.booking_id || n.url) setOpen(false);
                    void openEntry(n);
                  }}
                >
                  <p class={`text-sm font-medium ${isUnread(n) ? 'text-text' : 'text-gray-400'}`}>
                    {n.title}
                  </p>
                  <p class="text-xs text-gray-500 mt-0.5">{n.message}</p>
                </button>
              </li>
            ))}
          </ul>
          {/* 🔴 084 FR-009: offered whatever the read did, and whatever it returned. This panel
              holds at most 20 entries, so it is never the whole history — and gating the link on
              `items.length` would remove it precisely where a renter needs it most, since `data`
              is null on every non-`ok` arm. It leads to their history, not to "page 2"
              (spec Assumption 3).

              FR-010: the panel closes. The bell is mounted in the app shell (`main.tsx`), so it
              survives the navigation — a link that only routed would leave the panel sitting over
              the screen it just opened. */}
          <button
            type="button"
            class="w-full px-4 py-3 text-sm text-primary text-center border-t border-gray-800 hover:bg-surface-hi transition-colors"
            onClick={() => {
              setOpen(false);
              route('/account/notifications');
            }}
          >
            See all notifications
          </button>
        </div>
      )}
    </div>
  );
}
